import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from 'src/components/atoms/loading-button/loading-button';
import PasswordInput from 'src/components/atoms/password-input/password-input';
import AuthFormHeader from 'src/features/auth/components/organisms/auth-form-header';
import { useSetNewCredential } from 'src/hooks/api/auth/use-set-new-credential';
import { useKeyDown } from 'src/hooks/use-key-down';
import { Logo } from 'src/icons/logo';
import { Route } from 'src/routes/(common)/reset-password';
import { ApiError } from 'src/types/interfaces/api-error';
import { z } from 'zod';
import AuthFormWrapper from './auth-form-wrapper';

interface FormValues {
  password: string;
  passwordConfirmation: string;
}

const defaultValues: FormValues = {
  password: '',
  passwordConfirmation: '',
};

const UpdatePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { redirectAfterCompleteUrl, token } = Route.useSearch();

  const validationSchema = z
    .object({
      password: z
        .string()
        .min(
          8,
          t('password:changePassword.validation.minChar', {
            chars: 8,
          }),
        )
        .regex(/[0-9]/, t('password:changePassword.validation.atLeast1Number'))
        .regex(
          /[a-z]/,
          t('password:changePassword.validation.atLeast1LowerCaseLetter'),
        )
        .regex(
          /[A-Z]/,
          t('password:changePassword.validation.atLeast1UpperCaseLetter'),
        ),
      passwordConfirmation: z
        .string()
        .min(1, t('password:changePassword.validation.required')),
    })
    .refine(data => data.password === data.passwordConfirmation, {
      message: t('password:changePassword.validation.passwordMustMatch'),
      path: ['passwordConfirmation'],
    });

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    setError,
    handleSubmit,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const { mutateAsync: updatePassword } = useSetNewCredential(() => {
    toast.success(t('auth:forgotPassword.resetSuccess'));

    window.location.replace(redirectAfterCompleteUrl);
  });

  const onSubmit = async ({ password }: FormValues) => {
    if (!token) {
      return;
    }

    try {
      await updatePassword({ password, token });
    } catch (error) {
      const authError = error as ApiError;

      setError('root', { message: authError?.response.data.message });
    }
  };

  useKeyDown('Enter', errors.root ? () => {} : handleSubmit(onSubmit));

  return (
    <AuthFormWrapper
      actionsSlot={
        errors.root ? (
          <Button
            variant="contained"
            size="medium"
            onClick={() =>
              navigate({
                to: '/forgot-password',
              })
            }>
            {t('auth:forgotPassword.resetButton')}
          </Button>
        ) : (
          <LoadingButton
            variant="contained"
            size="medium"
            disabled={!isValid}
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}>
            {t('auth:resetPassword.resetPassword')}
          </LoadingButton>
        )
      }>
      <>
        <AuthFormHeader
          title={t('auth:resetPassword.title')}
          description={t('auth:resetPassword.content')}
          icon={<Logo />}
        />
        <Box>
          <Controller
            name="password"
            control={control}
            render={({
              field: { onChange, onBlur, value, ref },
              fieldState: { error },
            }) => (
              <PasswordInput
                label={t('auth:password')}
                fullWidth
                required
                inputRef={ref}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                type="password"
                autoComplete="password"
                error={!!error}
                helperText={error?.message}
                sx={{ mb: 2 }}
                inputProps={{
                  'data-testid': 'password-input',
                }}
              />
            )}
          />
          <Controller
            name="passwordConfirmation"
            control={control}
            render={({
              field: { onChange, onBlur, value, ref },
              fieldState: { error },
            }) => (
              <PasswordInput
                label={t('auth:passwordConfirm')}
                fullWidth
                required
                inputRef={ref}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                type="password"
                autoComplete="new-password"
                error={!!error}
                helperText={error?.message}
                sx={{ mb: 2 }}
                inputProps={{
                  'data-testid': 'confirm-password-input',
                }}
              />
            )}
          />
          {errors?.root?.message ? (
            <Alert severity="error">{errors?.root?.message}</Alert>
          ) : null}
        </Box>
      </>
    </AuthFormWrapper>
  );
};

export default UpdatePassword;
